import React, { useEffect, useRef, useState } from "react";

import { brandsService } from "../../services/brands";
import { SocialLink } from "../../components/brand/SocialLink";
import { PromotionRatingLabel } from "../../components/brand/PromotionRatingLabel";
import {
  PROFILE_FIELDS,
  PROMOTION_COPY,
} from "../../components/brand/constants";
import { PLATFORMS_ARRAY } from "../../utils/platforms";

const Profile = ({ brand, setBrand, hash }) => {
  const [editForm, setEditForm] = useState({});
  const initializedBrandIdRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [addPlatform, setAddPlatform] = useState("instagram");
  const [addHandle, setAddHandle] = useState("");
  const [addValidating, setAddValidating] = useState(false);
  const [addError, setAddError] = useState(null);
  const [addWarning, setAddWarning] = useState(null);
  const [addSuccessMessage, setAddSuccessMessage] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [savingPromotion, setSavingPromotion] = useState(false);

  useEffect(() => {
    if (!brand) return;
    if (initializedBrandIdRef.current === brand.id) return;
    initializedBrandIdRef.current = brand.id;
    const profileFieldValues = Object.fromEntries(
      PROFILE_FIELDS.map(({ key }) => [key, brand[key] ?? ""]),
    );
    setEditForm({
      name: brand.name ?? "",
      domain: brand.domain ?? "",
      promotion_rating: brand.promotion_rating ?? 5,
      ...profileFieldValues,
    });
  }, [brand]);

  const handleFieldChange = (key, value) => {
    setEditForm((prev) => ({ ...prev, [key]: value }));
  };

  const handlePromotionChange = (value) => {
    const num = Math.min(10, Math.max(0, Number(value)));
    setEditForm((prev) => ({ ...prev, promotion_rating: num }));
    setSavingPromotion(true);
    brandsService.updateBrand(
      hash,
      { promotion_rating: num },
      (response) => {
        setBrand(response.data);
        setSavingPromotion(false);
      },
      () => setSavingPromotion(false),
    );
  };

  const handleSave = () => {
    setSaveError(null);
    setSaving(true);
    const payload = { ...editForm };
    const pr = Number(payload.promotion_rating);
    payload.promotion_rating =
      !Number.isNaN(pr) && pr >= 0 && pr <= 10 ? pr : null;
    brandsService.updateBrand(
      hash,
      payload,
      (response) => {
        setBrand(response.data);
        setSaving(false);
      },
      (err) => {
        setSaveError(err?.response?.data?.detail || "Failed to save.");
        setSaving(false);
      },
    );
  };

  const handleAddCreator = () => {
    const trimmed = (addHandle || "").trim().replace(/^@/, "");
    if (!trimmed) return;

    const alreadyLinked = (brand.creators || []).some(
      (c) =>
        c.platform === addPlatform &&
        (c.platform_id || "").toLowerCase() === trimmed.toLowerCase(),
    );
    if (alreadyLinked) {
      setAddError("This creator is already linked.");
      return;
    }

    setAddError(null);
    setAddWarning(null);
    setAddValidating(true);
    brandsService.validateInputs(
      { creators: [{ platform: addPlatform, handle: trimmed }] },
      (response) => {
        const result = response.data?.creators?.[0];
        if (!result?.valid) {
          setAddValidating(false);
          setAddError(result?.error || "Could not find this channel.");
          return;
        }
        if (result.warning) setAddWarning(result.warning);
        brandsService.addCreator(
          hash,
          { platform: addPlatform, handle: trimmed },
          (resp) => {
            setAddValidating(false);
            setAddHandle("");
            setAddError(null);
            setAddWarning(null);
            setAddSuccessMessage(true);
            setShowAddForm(false);
            setBrand(resp.data);
          },
          (err) => {
            setAddValidating(false);
            const msg =
              err?.response?.data?.error ||
              "Failed to add creator. Please try again.";
            setAddError(msg);
          },
        );
      },
      () => {
        setAddValidating(false);
        setAddError("Validation failed. Please try again.");
      },
    );
  };

  const handleRemoveCreator = (platform, platformId) => {
    const key = `${platform}-${platformId}`;
    setRemoving(key);
    brandsService.removeCreator(
      hash,
      { platform, handle: platformId },
      (resp) => {
        setRemoving(null);
        setBrand(resp.data);
      },
      (err) => {
        setRemoving(null);
        setSaveError(err?.response?.data?.error || "Failed to remove account.");
      },
    );
  };

  if (!brand) return null;

  return (
    <div className="max-w-3xl space-y-6">
      <div className="text-theme-text">
        <h2 className="text-xl font-semibold mb-2">Profile</h2>
        <p className="text-sm text-theme-text opacity-70 mb-6">
          Edit brand name, website, and profile fields used for idea and script
          generation.
        </p>
      </div>

      {saveError && (
        <div className="p-3 bg-red-500 bg-opacity-20 border border-red-500 rounded text-sm text-theme-text">
          {saveError}
        </div>
      )}

      {addSuccessMessage && (
        <div className="p-3 bg-green-500 bg-opacity-20 border border-green-500 rounded text-sm text-theme-text">
          Social account linked. To re-import profile information, open Brand
          Settings and press &quot;Resync&quot;.
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-theme-text mb-1">
          Brand name
        </label>
        <input
          type="text"
          value={editForm.name ?? ""}
          onChange={(e) => handleFieldChange("name", e.target.value)}
          className="w-full px-3 py-2 rounded border border-theme-secondary bg-theme-main text-theme-text"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-theme-text mb-1">
          <PromotionRatingLabel tooltipPosition="right" />
        </label>
        <input
          type="range"
          min="0"
          max="10"
          value={editForm.promotion_rating ?? 5}
          onChange={(e) =>
            handleFieldChange("promotion_rating", e.target.value)
          }
          onMouseUp={(e) => handlePromotionChange(e.target.value)}
          onTouchEnd={(e) => handlePromotionChange(e.target.value)}
          disabled={savingPromotion}
          className="w-full accent-theme-primary"
        />
        <p className="text-sm text-theme-text opacity-80 mt-1">
          {editForm.promotion_rating ?? 5}/10 -{" "}
          {PROMOTION_COPY[editForm.promotion_rating ?? 5] ?? PROMOTION_COPY[5]}
        </p>
      </div>
      <div>
        <label className="block text-sm font-medium text-theme-text mb-1">
          Website URL
        </label>
        <input
          type="url"
          value={editForm.domain ?? ""}
          onChange={(e) => handleFieldChange("domain", e.target.value)}
          className="w-full px-3 py-2 rounded border border-theme-secondary bg-theme-main text-theme-text"
        />
      </div>

      <div>
        <div className="flex items-center gap-2 mb-2">
          <label className="block text-sm font-medium text-theme-text">
            Social accounts
          </label>
          {!showAddForm && (
            <button
              type="button"
              onClick={() => setShowAddForm(true)}
              className="text-sm text-theme-primary hover:underline font-medium"
            >
              + New
            </button>
          )}
        </div>
        {(brand.creators || []).length > 0 && (
          <div className="mb-3 space-y-2">
            {brand.creators.map((c) => {
              const rowKey = `${c.platform}-${c.platform_id}`;
              const isRemoving = removing === rowKey;
              return (
                <div
                  key={rowKey}
                  className="flex items-center gap-2 px-3 py-2 rounded border border-theme-secondary bg-theme-secondary text-sm text-theme-text"
                >
                  <SocialLink
                    platform={c.platform}
                    platformId={c.platform_id}
                    className="flex-1 min-w-0"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      handleRemoveCreator(c.platform, c.platform_id)
                    }
                    disabled={isRemoving}
                    className="shrink-0 text-theme-text opacity-60 hover:opacity-100 disabled:opacity-50 text-sm"
                    title="Unlink from brand"
                  >
                    {isRemoving ? "Removing..." : "Remove"}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {showAddForm && (
          <div className="rounded border border-theme-secondary bg-theme-main p-3 space-y-2">
            <div className="flex gap-2 flex-wrap">
              {PLATFORMS_ARRAY.map((platform) => (
                <button
                  key={platform.id}
                  type="button"
                  onClick={() => setAddPlatform(platform.id)}
                  className={`px-3 py-1.5 rounded border text-xs ${
                    addPlatform === platform.id
                      ? "border-theme-primary bg-theme-secondary"
                      : "border-theme-secondary opacity-70"
                  } text-theme-text`}
                  style={{
                    borderColor:
                      addPlatform === platform.id && platform.color
                        ? platform.color
                        : undefined,
                  }}
                >
                  {platform.name}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={addHandle}
                onChange={(e) => {
                  setAddHandle(e.target.value);
                  setAddError(null);
                  setAddWarning(null);
                  setAddSuccessMessage(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddCreator();
                  }
                }}
                placeholder={
                  addPlatform === "youtube"
                    ? "Channel handle or URL"
                    : "@handle"
                }
                className="flex-1 px-3 py-2 rounded border border-theme-secondary bg-theme-main text-theme-text placeholder-theme-text placeholder-opacity-50 text-sm"
              />
              <button
                type="button"
                onClick={handleAddCreator}
                disabled={!addHandle.trim() || addValidating}
                className="px-4 py-2 border border-theme-secondary rounded text-sm text-theme-text hover:bg-theme-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {addValidating ? "Checking..." : "Add"}
              </button>
              <button
                type="button"
                onClick={() => setShowAddForm(false)}
                className="px-4 py-2 rounded text-sm text-theme-text opacity-70 hover:opacity-100"
              >
                Cancel
              </button>
            </div>
            {addError && <p className="text-sm text-red-400">{addError}</p>}
            {addWarning && (
              <p className="text-sm text-amber-500 font-medium">{addWarning}</p>
            )}
          </div>
        )}
      </div>
      {PROFILE_FIELDS.map(({ key, label }) => (
        <div key={key}>
          <label className="block text-sm font-medium text-theme-text mb-1">
            {label}
          </label>
          <textarea
            value={editForm[key] ?? ""}
            onChange={(e) => handleFieldChange(key, e.target.value)}
            rows={3}
            className="w-full px-3 py-2 rounded border border-theme-secondary bg-theme-main text-theme-text"
          />
        </div>
      ))}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 bg-theme-primary text-theme-button rounded-md hover:opacity-90 disabled:opacity-50"
        >
          {saving ? "Saving..." : "Save changes"}
        </button>
      </div>
    </div>
  );
};

export default Profile;
